import { useState } from "react";

function ExternalCurriculum() {
  const [activeTab, setActiveTab] = useState("overview");

  const curricula = [
    {
      id: "acellus",
      name: "Acellus",
      description: "Video-based online curriculum with self-paced learning. Covers all subjects K-12 with interactive lessons and assessments.",
      website: "https://www.acellusacademy.com",
      features: ["Video Lessons", "Self-Paced", "Progress Tracking", "Accredited"],
      subjects: ["Math", "Science", "English", "History", "Foreign Languages"],
      grades: "K-12",
      color: "#4A90D9"
    },
    {
      id: "abeka",
      name: "Abeka",
      description: "Rigorous academic curriculum that can be adapted for Yisrael families. Use the strong math, language arts, and science foundations while supplementing with YLC's authentic scriptural teachings and heritage education.",
      website: "https://www.abeka.com",
      features: ["Strong Academics", "Textbooks + Video", "Adaptable Content", "Teacher Support"],
      subjects: ["Math", "Language Arts", "Science", "History (supplement with YLC)"],
      grades: "PreK-12",
      color: "#C41E3A",
      hebrewIsraeliteNote: "While Abeka provides excellent academics, Yisrael families should supplement the Bible and History portions with YLC's authentic teachings on the Twelve Tribes, true Hebrew heritage, and scriptural truth from our perspective."
    },
    {
      id: "time4learning",
      name: "Time4Learning",
      description: "Award-winning online curriculum with automated grading and progress reports. Interactive and engaging for students.",
      website: "https://www.time4learning.com",
      features: ["Automated Grading", "Progress Reports", "Interactive", "Flexible Schedule"],
      subjects: ["Language Arts", "Math", "Science", "Social Studies"],
      grades: "PreK-12",
      color: "#FF6B35"
    }
  ];

  const integrationTips = [
    {
      title: "Combine with YLC Scripture Studies",
      description: "Use external curricula for core academics while YLC provides in-depth Biblical and Yisrael education.",
      icon: "📚"
    },
    {
      title: "Track All Learning in Attendance",
      description: "Log hours from both YLC and external curricula in the Attendance Tracker for complete homeschool records.",
      icon: "📊"
    },
    {
      title: "Create a Balanced Schedule",
      description: "Morning: Core academics (Acellus/Abeka/T4L). Afternoon: Scripture studies and Hebrew lessons at YLC.",
      icon: "📅"
    },
    {
      title: "Use YLC for Enrichment",
      description: "YLC's unique heritage education complements standard curricula with spiritual depth and cultural identity.",
      icon: "✨"
    }
  ];

  return (
    <div className="card" data-testid="external-curriculum">
      <h3 style={{ color: "#FFD700", marginBottom: "10px" }}>
        External Curriculum Partners
      </h3>
      <p style={{ color: "#888", marginBottom: "25px" }}>
        Integrate top homeschool curricula with Yisrael Learning Center for a complete education.
      </p>

      {/* Tabs */}
      <div style={{
        display: "flex",
        gap: "10px",
        marginBottom: "25px",
        flexWrap: "wrap"
      }}>
        {[
          { id: "overview", label: "Overview" },
          { id: "acellus", label: "Acellus" },
          { id: "abeka", label: "Abeka" },
          { id: "time4learning", label: "Time4Learning" },
          { id: "tips", label: "Integration Tips" }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "12px 24px",
              background: activeTab === tab.id ? "#FFD700" : "#222",
              color: activeTab === tab.id ? "#000" : "#eee",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: activeTab === tab.id ? "bold" : "normal",
              transition: "all 0.2s"
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "20px"
        }}>
          {curricula.map(curr => (
            <div
              key={curr.id}
              style={{
                background: "#1a1a1a",
                borderRadius: "12px",
                padding: "25px",
                border: `2px solid ${curr.color}30`,
                transition: "transform 0.2s",
                cursor: "pointer"
              }}
              onClick={() => setActiveTab(curr.id)}
            >
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "15px"
              }}>
                <h4 style={{ color: curr.color, margin: 0, fontSize: "1.4rem" }}>
                  {curr.name}
                </h4>
                <span style={{
                  background: `${curr.color}20`,
                  color: curr.color,
                  padding: "4px 12px",
                  borderRadius: "20px",
                  fontSize: "0.8rem"
                }}>
                  {curr.grades}
                </span>
              </div>
              <p style={{ color: "#aaa", fontSize: "0.95rem", marginBottom: "15px", lineHeight: "1.6" }}>
                {curr.description}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {curr.features.slice(0, 3).map(feature => (
                  <span
                    key={feature}
                    style={{
                      background: "#222",
                      color: "#888",
                      padding: "5px 12px",
                      borderRadius: "15px",
                      fontSize: "0.8rem"
                    }}
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Individual Curriculum Tabs */}
      {curricula.map(curr => activeTab === curr.id && (
        <div key={curr.id}>
          <div style={{
            background: `linear-gradient(135deg, ${curr.color}15 0%, #1a1a1a 100%)`,
            borderRadius: "15px",
            padding: "30px",
            border: `2px solid ${curr.color}40`,
            marginBottom: "25px"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: "20px",
              marginBottom: "20px"
            }}>
              <div>
                <h3 style={{ color: curr.color, margin: "0 0 10px 0", fontSize: "2rem" }}>
                  {curr.name}
                </h3>
                <span style={{
                  background: `${curr.color}25`,
                  color: curr.color,
                  padding: "6px 16px",
                  borderRadius: "20px",
                  fontSize: "0.9rem"
                }}>
                  Grades {curr.grades}
                </span>
              </div>
              <a
                href={curr.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: curr.color,
                  color: "#fff",
                  padding: "15px 30px",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontWeight: "bold",
                  display: "inline-block"
                }}
              >
                Visit Website →
              </a>
            </div>
            <p style={{ color: "#ccc", fontSize: "1.1rem", lineHeight: "1.7", marginBottom: "25px" }}>
              {curr.description}
            </p>

            {/* Yisrael Note for Abeka */}
            {curr.hebrewIsraeliteNote && (
              <div style={{
                background: "linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(0,0,0,0.3) 100%)",
                border: "2px solid #FFD700",
                borderRadius: "10px",
                padding: "20px",
                marginBottom: "25px"
              }}>
                <h5 style={{ color: "#FFD700", margin: "0 0 10px 0", display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>✡</span> For Yisrael Families
                </h5>
                <p style={{ color: "#ddd", margin: 0, lineHeight: "1.7" }}>
                  {curr.hebrewIsraeliteNote}
                </p>
              </div>
            )}

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px"
            }}>
              <div>
                <h5 style={{ color: "#FFD700", marginBottom: "12px" }}>Key Features</h5>
                <ul style={{ margin: 0, paddingLeft: "20px", color: "#aaa" }}>
                  {curr.features.map(f => (
                    <li key={f} style={{ marginBottom: "8px" }}>{f}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h5 style={{ color: "#FFD700", marginBottom: "12px" }}>Subjects Offered</h5>
                <ul style={{ margin: 0, paddingLeft: "20px", color: "#aaa" }}>
                  {curr.subjects.map(s => (
                    <li key={s} style={{ marginBottom: "8px" }}>{s}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div style={{
            background: "#111",
            borderRadius: "10px",
            padding: "20px",
            border: "1px solid #333"
          }}>
            <h5 style={{ color: "#90EE90", marginBottom: "12px" }}>
              How to Use with Yisrael Learning Center
            </h5>
            <p style={{ color: "#888", margin: 0, lineHeight: "1.7" }}>
              {curr.id === "abeka" ? (
                <>
                  Use Abeka's strong academic foundation for Math, Language Arts, and Science. 
                  <strong style={{ color: "#FFD700" }}> Replace their Bible and History curriculum with YLC's authentic Yisrael teachings.</strong> This ensures your children receive rigorous academics while learning the true heritage, history, and scriptures of the Nation of Yisrael. 
                  Log all study hours in the Attendance Tracker to maintain compliant homeschool records.
                </>
              ) : (
                <>
                  {curr.name} provides excellent {curr.id === "acellus" ? "video-based" : "interactive"} academics. 
                  Pair it with YLC's unique Yisrael scripture studies, heritage education, and 
                  ordination programs for a complete spiritual and academic education. Log all study hours 
                  in the Attendance Tracker to maintain compliant homeschool records.
                </>
              )}
            </p>
          </div>
        </div>
      ))}

      {/* Integration Tips Tab */}
      {activeTab === "tips" && (
        <div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "20px",
            marginBottom: "30px"
          }}>
            {integrationTips.map((tip, index) => (
              <div
                key={index}
                style={{
                  background: "#1a1a1a",
                  borderRadius: "12px",
                  padding: "25px",
                  border: "1px solid #333"
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "15px" }}>{tip.icon}</div>
                <h4 style={{ color: "#FFD700", marginBottom: "10px" }}>{tip.title}</h4>
                <p style={{ color: "#888", margin: 0, lineHeight: "1.6" }}>{tip.description}</p>
              </div>
            ))}
          </div>

          <div style={{
            background: "linear-gradient(135deg, rgba(255,215,0,0.1) 0%, #1a1a1a 100%)",
            borderRadius: "15px",
            padding: "30px",
            border: "2px solid rgba(255,215,0,0.3)"
          }}>
            <h4 style={{ color: "#FFD700", marginBottom: "15px" }}>
              Sample Weekly Schedule
            </h4>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "15px"
            }}>
              {[
                { time: "8:00 - 10:00", activity: "Core Academics", source: "Acellus/Abeka/T4L" },
                { time: "10:00 - 10:30", activity: "Break", source: "" },
                { time: "10:30 - 12:00", activity: "Scripture Study", source: "YLC" },
                { time: "12:00 - 1:00", activity: "Lunch", source: "" },
                { time: "1:00 - 2:30", activity: "Hebrew/History", source: "YLC" },
                { time: "2:30 - 3:30", activity: "Electives/PE", source: "Various" }
              ].map((slot, i) => (
                <div
                  key={i}
                  style={{
                    background: slot.source ? "#222" : "transparent",
                    padding: slot.source ? "15px" : "10px",
                    borderRadius: "8px",
                    textAlign: "center"
                  }}
                >
                  <div style={{ color: "#FFD700", fontSize: "0.85rem", marginBottom: "5px" }}>
                    {slot.time}
                  </div>
                  <div style={{ color: "#eee", fontWeight: "500" }}>{slot.activity}</div>
                  {slot.source && (
                    <div style={{ color: "#888", fontSize: "0.8rem", marginTop: "5px" }}>
                      {slot.source}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExternalCurriculum;
